import { cva, type VariantProps } from 'class-variance-authority';
import { disabledState, focusRing, transitionBase, uiMarker } from './shared';

/**
 * BUTTON
 *
 * `variant` is about intent (how loud is this action?), `size` is about the
 * control height it snaps to. Heights come from --control-height-* so a button
 * and an input placed side by side always line up.
 *
 * The `icon` size is a square button for an icon on its own — it still needs an
 * aria-label, which the Button component enforces in development.
 */
export const buttonRecipe = cva(
  [
    uiMarker,
    'inline-flex items-center justify-center gap-2 whitespace-nowrap align-middle',
    'rounded-md font-sans text-body font-medium',
    'select-none',
    // Any icon passed as a child is sized here so callers never have to.
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    transitionBase,
    focusRing,
    disabledState,
    // The href form renders an <a>, which never matches :disabled — it says
    // aria-disabled instead, and has to look and behave the same.
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
  ],
  {
    variants: {
      /*
       * Each variant carries a `data-[force=hover]` twin of its hover rule, so
       * the catalogue can show the hover state without a pointer. Written out
       * rather than derived because Tailwind reads class names from source.
       */
      variant: {
        /** The one main action on a screen. Use at most one per view. */
        primary:
          'bg-primary text-primary-foreground shadow-e1 hover:bg-primary/90 data-[force=hover]:bg-primary/90',
        /**
         * A supporting action that still deserves a filled button: a faint
         * wash of the page's ink, no border. Also the phone's round buttons —
         * back and more in a call's header, directions and call beside a
         * value, the close on a sheet — which the Figma draws as 5% black;
         * the glass behind it keeps it legible over a map.
         */
        secondary:
          'bg-foreground/5 text-foreground backdrop-blur-glass hover:bg-foreground/10 data-[force=hover]:bg-foreground/10',
        /** Equal-weight action next to a primary one. */
        outline:
          'border border-input bg-background text-foreground shadow-e1 hover:bg-accent hover:text-accent-foreground data-[force=hover]:bg-accent data-[force=hover]:text-accent-foreground',
        /** Lowest-emphasis action: toolbars, table row actions, icon buttons. */
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground data-[force=hover]:bg-accent data-[force=hover]:text-accent-foreground',
        /** Deletes, cancellations, anything the user cannot undo. */
        destructive:
          'bg-destructive text-destructive-foreground shadow-e1 hover:bg-destructive/90 data-[force=hover]:bg-destructive/90',
        /*
         * The sales page's own buttons. It is drawn to a brand of its own
         * (`.theme-sales`), so its buttons are named for it rather than shared
         * with the product: a change to the product's buttons must not reach
         * a page prospects are reading.
         */
        /**
         * The black "Get started" in the sales page's header: --secondary is
         * near-black in the sales theme.
         */
        'sales-dark':
          'bg-secondary text-secondary-foreground shadow-e1 hover:bg-secondary/80 data-[force=hover]:bg-secondary/80',
        /**
         * A light button on a filled primary panel: the last "Get started"
         * on the sales page's closing band, where primary would vanish.
         */
        'sales-light':
          'bg-card text-card-foreground shadow-e1 hover:bg-card/90 data-[force=hover]:bg-card/90',
        /**
         * The second action on a filled primary panel, next to a sales-light
         * button: "Book a call" beside "Get started" on the sales page.
         */
        'sales-tint':
          'bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 data-[force=hover]:bg-primary-foreground/20',
        /** Looks like a link, behaves like a button. */
        link: 'text-primary underline-offset-4 hover:underline data-[force=hover]:underline',
        /**
         * A ghost that starts muted and comes up to full colour on hover —
         * the close, dismiss and "more" buttons at the edge of a panel.
         * Thirty-two sites wrote this as ghost plus two colour classes.
         */
        subtle:
          'text-muted-foreground hover:bg-muted hover:text-foreground data-[force=hover]:bg-muted data-[force=hover]:text-foreground',
        /** A ghost for a destructive row action: red text, no fill until hover. */
        'destructive-ghost':
          'text-destructive hover:bg-destructive-subtle hover:text-destructive data-[force=hover]:bg-destructive-subtle',
        /**
         * Secondary's weight for a destructive action: a faint red fill and
         * red words, as the Figma's photo sheet draws Delete beside Comment.
         * As loud as the secondary beside it, so the pair read as two choices.
         */
        'destructive-secondary':
          'bg-destructive-subtle text-destructive hover:bg-destructive/15 data-[force=hover]:bg-destructive/15',
        /**
         * A control laid over a picture — the remove button on an uploaded
         * photo. A dark scrim with a white glyph in either theme, for the
         * reason a lightbox is dark in both: it sits on the image, not the
         * page, and a scrim that flipped with the theme would vanish on a
         * light photo in dark mode.
         */
        /**
         * No chrome in any state. For a clickable region whose container
         * already carries the affordance — the title block of a card that
         * expands, the day label beside a switch — where a ghost's hover
         * fill would paint a box around one part of a row.
         */
        plain: 'text-foreground',
        overlay:
          // design-tokens-ignore -- sits on a photo, not on a themed surface; see above.
          'bg-black/60 text-white hover:bg-black/75 hover:text-white data-[force=hover]:bg-black/75',
        /**
         * The action at the top of a phone screen, beside its heading: the
         * calls list's "+ Call". A soft green wash rising to white with a
         * faint brand rule, as the Figma draws it, so it reads as the way in
         * without the weight of a filled primary.
         */
        soft: 'border border-soft-border bg-gradient-to-t from-soft-from to-soft-to text-foreground shadow-e1 hover:brightness-[0.98] data-[force=hover]:brightness-[0.98]',
        /**
         * An editable value, drawn as the box a read-only one sits in.
         *
         * The impound detail tab shows each field as a filled box that
         * becomes an input when clicked. It matches readOnlyValueRecipe —
         * same fill, minimum height, inset and weight — so an editable value
         * and a fixed one read as the same kind of thing, and only trying
         * tells them apart. The height comes from the content so a joined
         * list can wrap.
         */
        value:
          'min-h-control-md justify-start whitespace-normal bg-muted/50 text-left font-normal text-foreground hover:bg-muted data-[force=hover]:bg-muted',
      },
      /** Fully rounded ends — chips, the studio's floating controls. */
      pill: {
        true: 'rounded-pill',
        false: '',
      },
      size: {
        sm: 'h-control-sm px-3 text-body-sm [&_svg]:size-icon-sm',
        md: 'h-control-md px-4 [&_svg]:size-icon-sm',
        lg: 'h-control-lg px-6 [&_svg]:size-icon-md',
        /** A full-page form's primary action. Signup and onboarding use it. */
        xl: 'h-control-xl px-8 text-body-lg [&_svg]:size-icon-md',
        /**
         * Content-shaped: no height, no padding.
         *
         * For a trigger with no chrome — an icon in a table cell, a word that
         * acts as a link. 64 of these were written as `h-auto p-0`, which is
         * this variant spelled by hand, and which the height check could not
         * tell apart from a control that had simply opted out of the scale.
         */
        inline: 'h-auto p-0 [&_svg]:size-icon-sm',
        /** Square, icon-only. */
        icon: 'h-control-md w-control-md p-0 [&_svg]:size-icon-sm',
        'icon-xs': 'h-control-xs w-control-xs p-0 [&_svg]:size-icon-xs',
        'icon-sm': 'h-control-sm w-control-sm p-0 [&_svg]:size-icon-sm',
        'icon-lg': 'h-control-lg w-control-lg p-0 [&_svg]:size-icon-md',
        /**
         * A bare glyph at 24, the Figma Sheet Header's close, in the 40 a
         * thumb needs to hit: drawn at icon-lg's 20 it read a size small.
         */
        'icon-lg-glyph': 'h-control-lg w-control-lg p-0 [&_svg]:size-icon-lg',
      },
      /** Stretch to fill the container. Useful in mobile sheets and forms. */
      /**
       * Let the height come from the content.
       *
       * `size` keeps meaning padding and type — this only drops the fixed
       * height, for a button whose label wraps or that stacks two lines. 63 of
       * these were written as `h-auto` plus hand-picked padding, which tangled
       * two decisions into one className and made a growing button
       * indistinguishable from one that had simply opted out of the scale.
       */
      grow: {
        true: 'h-auto',
        false: '',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
      /**
       * Hidden until the row or frame it sits in is hovered — the remove
       * button on an uploaded photo, the hide control on a rail row. The
       * parent carries `group`. Same meaning as DragHandle's `reveal`; six
       * sites wrote it out as three opacity classes.
       *
       * A revealed trigger stays shown while what it opened is open: the
       * pointer leaves the row for the popover, and the button that opened
       * it must not vanish underneath. Radix stamps `data-state="open"` on
       * the trigger, which is the signal.
       */
      reveal: {
        true: 'opacity-0 transition-all group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
        false: '',
      },
    },
    compoundVariants: [
      // A link-styled button should not carry button padding or height.
      // The Figma's "+ Call": 40 high, the plus 4px from the word, 12px in.
      { variant: 'soft', size: 'lg', class: 'gap-1 px-3' },
      // The call screen's "Call activity": the same 40-high pill, in the wash.
      // Pills only: a square secondary at the desk keeps the scale's inset.
      { variant: 'secondary', size: 'lg', pill: true, class: 'gap-1 px-3' },
      // A call's Add, beside its status, and the rows of the sheets it and
      // the pickup open (Figma 721-23588, 895-20367): 48 high, the glyph
      // 4px from the word, 16px in.
      { variant: 'secondary', size: 'xl', pill: true, class: 'gap-1 px-4' },
      { variant: 'link', size: 'sm', class: 'h-auto px-0' },
      { variant: 'link', size: 'md', class: 'h-auto px-0' },
      { variant: 'link', size: 'lg', class: 'h-auto px-0' },
      // A value box grows with its content and sits on the read-only inset.
      { variant: 'value', size: 'sm', class: 'h-auto px-3 py-2' },
      { variant: 'value', size: 'md', class: 'h-auto px-3 py-2' },
      { variant: 'value', size: 'lg', class: 'h-auto px-3 py-2' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      grow: false,
      fullWidth: false,
      pill: false,
      reveal: false,
    },
  }
);

export type ButtonRecipeProps = VariantProps<typeof buttonRecipe>;
